import { tenantNotFoundStateName } from 'modules/application/tenant';
import { emitter } from 'data/function';
import Semaphore from 'semaphore-async-await';
const OnDefineContextEvent = 'onDefineContext';
export var Contexts;
(function (Contexts) {
    Contexts["Default"] = "default";
    Contexts["Tenants"] = "tenants";
})(Contexts || (Contexts = {}));
export class ContextProcessorService {
    static $inject = ['TenantService', 'BLIP_DOMAIN_URL', '$state', '$timeout', 'PORTALDOMAIN', 'AuthenticationService', 'AccountService2'];
    constructor(TenantService, BLIP_DOMAIN_URL, $state, $timeout, PORTALDOMAIN, AuthenticationService, AccountService2) {
        'ngInject';
        this.TenantService = TenantService;
        this.BLIP_DOMAIN_URL = BLIP_DOMAIN_URL;
        this.$state = $state;
        this.$timeout = $timeout;
        this.PORTALDOMAIN = PORTALDOMAIN;
        this.AuthenticationService = AuthenticationService;
        this.AccountService2 = AccountService2;
        this.isContextDefined = false;
        this.createInitializationPromise();
        this.lock = new Semaphore(1);
    }
    set tenant(value) {
        this._tenant = value;
        this.isContextDefined = true;
        emitter.emit(OnDefineContextEvent);
    }
    get tenant() {
        return this._tenant || { id: undefined, name: undefined };
    }
    /**
     * This method returns a resolved promise when client is connected
     */
    onDefine(fn) {
        if (!this.isContextDefined) {
            // Its become necessary due to be an external event thats not handled by angular lifecycle
            emitter.on(OnDefineContextEvent, () => this.$timeout(fn));
        }
        else {
            this.$timeout(fn);
        }
    }
    getCurrentTenant() {
        return this.waitForInitialization().then(() => this.tenant);
    }
    getCurrentTenantCluster() {
        return this.waitForInitialization().then(() => this.tenantCluster);
    }
    getCurrentTenantPlan() {
        return this.waitForInitialization().then(() => this.tenantPlan);
    }
    createInitializationPromise() {
        this.waitForInitializationPromise = {
            resolve: () => undefined,
            reject: () => undefined,
            promise: new Promise(() => undefined),
        };
        const promise = new Promise((resolve, reject) => {
            this.waitForInitializationPromise.resolve = resolve;
            this.waitForInitializationPromise.reject = reject;
        });
        this.waitForInitializationPromise.promise = promise;
    }
    async waitForInitialization() {
        if (!this.context) {
            await this.define();
        }
        return this.waitForInitializationPromise.promise;
    }
    checkDomain() {
        const domain = window.location.host;
        if (!this.BLIP_DOMAIN_URL.slice(0, -1).includes(domain)) {
            this.$state.go(tenantNotFoundStateName);
        }
    }
    async getTenantDetail() {
        if (!this.tenant.id) {
            return;
        }
        this.tenantPlan = await this.TenantService.getTenantPlan(this.tenant.id);
        this.tenantCluster = await this.TenantService.getTenantClusterNameByTenantId(this.tenant.id);
    }
    async define() {
        const setLastTenantViewed = async (user, tenant) => {
            const lastUsedTenantsExtra = user.extras && user.extras.lastUsedTenants || '[]';
            const lastUsedTenants = [...JSON.parse(lastUsedTenantsExtra).filter((t) => t !== tenant.id), tenant.id];
            await this.AccountService2.mergeExtras({
                lastUsedTenants: JSON.stringify(lastUsedTenants.slice(lastUsedTenants.length - 10, lastUsedTenants.length)),
            });
        };
        const setupContext = async () => {
            const domain = window.location.host;
            this.currentUser = await this.AccountService2.me();
            try {
                await this.lock.wait();
                if (this.context) {
                    return;
                }
                const urlredirectDefaultContract = this.AuthenticationService.auth.redirectDefaultContract;
                this.AuthenticationService.auth = { ...this.AuthenticationService.auth, redirectDefaultContract: false };
                /**
                 * In this case, user will be in a bot context
                 */
                const isOrganizationEnabled = await this.TenantService.isOrganizationEnabled();
                if (!isOrganizationEnabled || this.BLIP_DOMAIN_URL.slice(0, -1).includes(domain)) {
                    this.tenant = await this.TenantService.getPersonalTenant();
                    this.context = Contexts.Default;
                    await setLastTenantViewed(this.currentUser, this.tenant);
                    this.waitForInitializationPromise.resolve();
                    try {
                        const tenandUser = await this.TenantService.getTenantUserDefaultContract();
                        if (urlredirectDefaultContract && tenandUser != undefined) {
                            window.location.href = this.TenantService.getTenantUrl(tenandUser.tenantId, true);
                        }
                    }
                    catch (e) {
                        const err = JSON.parse(e);
                        if (err.reason.code !== 67) {
                            console.error('Error: ', err);
                        }
                    }
                    return;
                }
            }
            catch (e) {
                console.error(e);
            }
            finally {
                this.lock.signal();
            }
            /**
             * User probably in a tenant context
             */
            try {
                const tenantId = domain.split('.')[0];
                const tenant = await this.TenantService.get(tenantId);
                this.tenant = tenant;
                await this.getTenantDetail();
                if (this.AuthenticationService.auth.extras?.externalLogin) {
                    await this.TenantService.setGrantExternalLoginAccess(tenantId, this.AuthenticationService.auth.token);
                }
                await setLastTenantViewed(this.currentUser, tenant);
            }
            catch (e) {
                this.tenant = await this.TenantService.getPersonalTenant();
                this.$state.go(tenantNotFoundStateName);
                console.error(e);
            }
            finally {
                this.context = Contexts.Tenants;
                this.waitForInitializationPromise.resolve();
            }
        };
        /**
         * Wait for initialize BlipService
         * The "then" syntax is used here because we don't want to block code execution
         * waiting for anything. We want to execute this as a callback when BlipService
         * connection is ready.
         */
        this.AccountService2
            .connect()
            .then(setupContext);
    }
}
ContextProcessorService.$inject = ['TenantService', 'BLIP_DOMAIN_URL', '$state', '$timeout', 'PORTALDOMAIN', 'AuthenticationService', 'AccountService2'];
